import React, { useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";

/**
 * Bouton d'export élégant façon Artsy.com - VERSION SÉCURISÉE
 * Export CSV pur texte sans liens Supabase
 * Pour les images, utilisez le système de rapports PDF existant
 */
const ExportButton = ({ 
  collectionId = null, 
  label = "Exporter", 
  variant = "btn-secondary-artsy",
  format = "csv"
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const { user } = useAuth();

  /**
   * Échappe les valeurs pour le CSV (gestion des guillemets et virgules)
   */
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  /**
   * Parse les détails d'acquisition (stockés en JSON)
   */
  const parseAcquisitionDetails = (acquisitionDetails) => {
    if (!acquisitionDetails) return { type: "", price: "", date: "", source: "" };
    
    try {
      const parsed = typeof acquisitionDetails === "string" 
        ? JSON.parse(acquisitionDetails) 
        : acquisitionDetails;
      
      return {
        type: parsed.type || parsed.acquisition_type || "",
        price: parsed.price || parsed.acquisition_price || "",
        date: parsed.acquisition_date || parsed.date || "",
        source: parsed.acquisition_source || parsed.source || ""
      };
    } catch (e) {
      console.warn("Impossible de parser acquisitionDetails:", e);
      return { type: "", price: "", date: "", source: "" };
    }
  };

  /**
   * Compte le nombre d'images pour une œuvre
   */
  const getImageCount = (images) => {
    if (!images) return 0;
    
    try {
      const parsed = typeof images === "string" ? JSON.parse(images) : images;
      if (Array.isArray(parsed)) {
        return parsed.length;
      }
    } catch (e) {
      return 0;
    }
    
    return 0;
  };

  /**
   * Parse les dimensions (stockées en JSON)
   */
  const parseDimensions = (specifications, dimensions) => {
    let dims = { height: "", width: "", depth: "", unit: "cm" };
    
    try {
      if (specifications) {
        const parsed = typeof specifications === "string" 
          ? JSON.parse(specifications) 
          : specifications;
        
        if (parsed.dimensions) {
          dims = {
            height: parsed.dimensions.height || "",
            width: parsed.dimensions.width || "",
            depth: parsed.dimensions.depth || "",
            unit: parsed.dimensions.unit || "cm"
          };
        }
      }
    } catch (e) {
      if (dimensions) {
        dims.display = dimensions;
      }
    }
    
    return dims;
  };

  /**
   * Formate les dimensions en texte lisible
   */
  const formatDimensions = (dims) => {
    const parts = [];
    if (dims.height) parts.push(`H ${dims.height}`);
    if (dims.width) parts.push(`L ${dims.width}`);
    if (dims.depth) parts.push(`P ${dims.depth}`);
    if (parts.length === 0) return "";
    return parts.join(" × ") + (dims.unit ? ` ${dims.unit}` : "");
  };

  /**
   * Parse les tags/catégories
   */
  const parseTags = (tags) => {
    if (!tags) return "";
    
    try {
      if (typeof tags === "string") {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) {
          return parsed.join(", ");
        }
      }
      if (Array.isArray(tags)) {
        return tags.join(", ");
      }
    } catch (e) {
      return tags;
    }
    
    return tags;
  };

  /**
   * Parse la provenance
   */
  const parseProvenance = (provenance) => {
    if (!provenance) return "";
    
    try {
      const parsed = typeof provenance === "string" ? JSON.parse(provenance) : provenance;
      if (Array.isArray(parsed)) {
        return parsed
          .map(p => `${p.date || ""}: ${p.description || ""}`)
          .join(" | ");
      }
    } catch (e) {
      return provenance;
    }
    
    return provenance;
  };

  /**
   * Formate une date au format français
   */
  const formatDate = (date) => {
    if (!date) return "";
    try {
      return new Date(date).toLocaleDateString("fr-FR");
    } catch (e) {
      return date;
    }
  };

  /**
   * Génère le CSV pour une collection - VERSION TEXTE PUR
   */
  const generateCollectionCsv = async (collection, artworks) => {
    const lines = [];
    
    // En-tête de la collection
    lines.push("=== INFORMATIONS COLLECTION ===");
    lines.push(`Titre,${escapeCsvValue(collection.title)}`);
    lines.push(`Artiste,${escapeCsvValue(collection.artist || "")}`);
    lines.push(`Description,${escapeCsvValue(collection.description || "")}`);
    lines.push(`Nombre d'œuvres,${artworks.length}`);
    
    // Calculer les totaux
    let totalEstimated = 0;
    let totalPurchase = 0;
    let totalWithImages = 0;
    
    artworks.forEach(art => {
      const estimated = parseFloat(art.value) || 0;
      totalEstimated += estimated;
      
      const acquisition = parseAcquisitionDetails(art.acquisitionDetails);
      const purchase = parseFloat(acquisition.price) || 0;
      totalPurchase += purchase;
      
      const imageCount = getImageCount(art.images);
      if (imageCount > 0) totalWithImages++;
    });
    
    lines.push(`Valeur estimée totale (€),${totalEstimated.toLocaleString("fr-FR")}`);
    lines.push(`Valeur d'achat totale (€),${totalPurchase.toLocaleString("fr-FR")}`);
    lines.push(`Œuvres avec photos,${totalWithImages} sur ${artworks.length}`);
    lines.push(`Date d'export,${formatDate(new Date())}`);
    lines.push("");
    lines.push("Note: Pour les images, utilisez le système de rapports PDF");
    lines.push(""); // Ligne vide
    
    // En-tête des œuvres
    const headers = [
      "Titre",
      "Artiste",
      "Année",
      "Medium/Technique",
      "Dimensions",
      "Valeur estimée (€)",
      "Prix d'achat (€)",
      "Date d'acquisition",
      "Source d'acquisition",
      "Type d'acquisition",
      "État",
      "Signature",
      "Édition",
      "Nb. photos",
      "Catégories",
      "Provenance",
      "Description"
    ];
    
    lines.push("=== LISTE DES ŒUVRES ===");
    lines.push(headers.map(h => escapeCsvValue(h)).join(","));
    
    // Données des œuvres
    artworks.forEach(artwork => {
      const acquisition = parseAcquisitionDetails(artwork.acquisitionDetails);
      const dims = parseDimensions(artwork.specifications, artwork.dimensions);
      const imageCount = getImageCount(artwork.images);
      const tags = parseTags(artwork.tags);
      const provenance = parseProvenance(artwork.provenance);
      
      const row = [
        artwork.title || "Sans titre",
        artwork.artist || "",
        artwork.year || artwork.creationDate || "",
        artwork.medium || artwork.typeOfArt || "",
        formatDimensions(dims),
        artwork.value || "",
        acquisition.price,
        formatDate(acquisition.date),
        acquisition.source,
        acquisition.type,
        artwork.condition || "",
        artwork.signature_desc || artwork.signature || "",
        artwork.isEdition === "true" || artwork.isEdition === true 
          ? (artwork.editionInfo || "Oui") 
          : "",
        imageCount,
        tags,
        provenance,
        artwork.description || ""
      ];
      
      lines.push(row.map(v => escapeCsvValue(v)).join(","));
    });
    
    return lines.join("\n");
  };

  /**
   * Exporte toutes les collections
   */
  const exportAllCollections = async () => {
    setIsExporting(true);
    
    try {
      // Récupérer toutes les collections de l'utilisateur
      const { data: collections, error: collectionsError } = await supabase
        .from("collections")
        .select("*")
        .eq("client_id", user.id)
        .order("title");
      
      if (collectionsError) throw collectionsError;
      
      if (!collections || collections.length === 0) {
        alert("Aucune collection à exporter");
        return;
      }
      
      // Récupérer toutes les œuvres de l'utilisateur
      const { data: allArtworks, error: artworksError } = await supabase
        .from("artworks")
        .select("*")
        .eq("client_id", user.id);
      
      if (artworksError) throw artworksError;
      
      // Calculer les statistiques globales
      let totalArtworks = allArtworks?.length || 0;
      let totalEstimated = 0;
      let totalPurchase = 0;
      let totalWithImages = 0;
      
      (allArtworks || []).forEach(art => {
        totalEstimated += parseFloat(art.value) || 0;
        const acq = parseAcquisitionDetails(art.acquisitionDetails);
        totalPurchase += parseFloat(acq.price) || 0;
        if (getImageCount(art.images) > 0) totalWithImages++;
      });
      
      // Générer le CSV global
      const csvParts = [];
      csvParts.push("═══════════════════════════════════════════════════════════════════════");
      csvParts.push(`EXPORT COMPLET - ${collections.length} COLLECTION(S)`);
      csvParts.push("═══════════════════════════════════════════════════════════════════════");
      csvParts.push(`Date d'export,${formatDate(new Date())}`);
      csvParts.push(`Client,${user.email || ""}`);
      csvParts.push("");
      csvParts.push("=== STATISTIQUES GLOBALES ===");
      csvParts.push(`Nombre total de collections,${collections.length}`);
      csvParts.push(`Nombre total d'œuvres,${totalArtworks}`);
      csvParts.push(`Valeur estimée totale (€),${totalEstimated.toLocaleString("fr-FR")}`);
      csvParts.push(`Valeur d'achat totale (€),${totalPurchase.toLocaleString("fr-FR")}`);
      csvParts.push(`Œuvres avec photos,${totalWithImages} sur ${totalArtworks}`);
      csvParts.push("");
      csvParts.push("═══════════════════════════════════════════════════════════════════════");
      csvParts.push("");
      
      // Pour chaque collection
      for (const collection of collections) {
        const artworks = allArtworks.filter(
          art => art.collection_id === collection.id
        );
        
        const collectionCsv = await generateCollectionCsv(collection, artworks);
        csvParts.push(collectionCsv);
        csvParts.push("");
        csvParts.push("═══════════════════════════════════════════════════════════════════════");
        csvParts.push("");
      }
      
      // Télécharger le fichier
      const csvContent = csvParts.join("\n");
      const blob = new Blob(["\ufeff" + csvContent], { 
        type: "text/csv;charset=utf-8;" 
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      const filename = `artkiv_toutes_collections_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      alert("Une erreur est survenue lors de l'export. Veuillez réessayer.");
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Exporte une collection spécifique
   */
  const exportSingleCollection = async () => {
    setIsExporting(true);
    
    try {
      // Récupérer la collection
      const { data: collection, error: collectionError } = await supabase
        .from("collections")
        .select("*")
        .eq("id", collectionId)
        .eq("client_id", user.id)
        .single();
      
      if (collectionError) throw collectionError;
      
      if (!collection) {
        alert("Collection introuvable");
        return;
      }
      
      // Récupérer les œuvres de la collection
      const { data: artworks, error: artworksError } = await supabase
        .from("artworks")
        .select("*")
        .eq("collection_id", collectionId)
        .eq("client_id", user.id);
      
      if (artworksError) throw artworksError;
      
      // Générer le CSV
      const csvContent = await generateCollectionCsv(
        collection, 
        artworks || []
      );
      
      // Télécharger le fichier
      const blob = new Blob(["\ufeff" + csvContent], { 
        type: "text/csv;charset=utf-8;" 
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      const safeTitle = collection.title
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase();
      const filename = `artkiv_${safeTitle}_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      alert("Une erreur est survenue lors de l'export. Veuillez réessayer.");
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Gère le clic sur le bouton
   */
  const handleExport = () => {
    if (!user) {
      alert("Vous devez être connecté pour exporter");
      return;
    }
    
    if (collectionId) {
      exportSingleCollection();
    } else {
      exportAllCollections();
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`btn-artsy ${variant} ${
        isExporting ? "opacity-50 cursor-not-allowed" : ""
      }`}
      title={collectionId ? "Exporter cette collection en CSV" : "Exporter toutes les collections en CSV"}
    >
      {isExporting ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 inline"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Export en cours...
        </>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4 inline mr-2 -mt-0.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
          {label}
        </>
      )}
    </button>
  );
};

export default ExportButton;